import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Truck, ShieldCheck, RotateCcw, Gift } from 'lucide-react';
import api from '@/lib/api';
import ProductRail from '@/components/common/ProductRail';
import Reveal from '@/components/common/Reveal';
import Loader from '@/components/common/Loader';
import Stars from '@/components/common/Stars';
import { useStore } from '@/context/StoreContext';

const RoomTile = ({ room }) => (
  <Link to={`/rooms/${room.slug}`} className="group relative block overflow-hidden rounded-2xl" data-testid="room-browse-card">
    <div className="aspect-[3/4] overflow-hidden">
      <img src={room.image} alt={room.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
    <div className="absolute bottom-4 left-4 right-4">
      <h3 className="font-display text-xl text-white">{room.name}</h3>
      <span className="mt-1 inline-flex items-center gap-1 text-xs text-white/80">Shop now <ArrowRight size={13} /></span>
    </div>
  </Link>
);

export default function Home() {
  const [data, setData] = useState(null);
  const { recent } = useStore();
  const [recentProducts, setRecentProducts] = useState([]);
  const [featuredCats, setFeaturedCats] = useState([]);

  useEffect(() => {
    api.get('/home').then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/categories/featured', { params: { limit: 8 } }).then((r) => setFeaturedCats(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (recent.length) api.post('/products/by-ids', { ids: recent }).then((r) => setRecentProducts(r.data)).catch(() => {});
  }, [recent]);

  // Set SEO title / description from admin SEO defaults
  useEffect(() => {
    if (!data?.seo) return;
    const seo = data.seo;
    if (seo.meta_title) document.title = seo.meta_title;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', seo.meta_description);
    return () => { document.title = 'Gaze Decor'; };
  }, [data]);

  const isMobile = useMemo(() => typeof window !== 'undefined' && window.innerWidth < 768, []);

  if (!data) return <Loader label="Curating luxury" />;
  const { hero = {}, brand_story = {}, video_banner = {}, testimonials = [], rooms = [], styles = [], inspiration = [], instagram = [], sections = [] } = data;
  const heroImage = (isMobile && hero.image_mobile) || hero.image;

  const enabledSections = sections.filter((s) => s.enabled !== false);

  const renderSection = (s) => {
    switch (s.key) {
      case 'trending':
        return <ProductRail key="trending" testId="rail-trending" eyebrow={s.eyebrow || 'Trending Now'} title={s.title || 'Pieces our clients are choosing'} products={(data.trending || []).slice(0, s.limit || 8)} viewAll="/collection/trending-now" />;
      case 'new_arrivals':
        return <ProductRail key="new_arrivals" testId="rail-new" eyebrow={s.eyebrow || 'Just In'} title={s.title || 'New Arrivals'} products={(data.new_arrivals || []).slice(0, s.limit || 8)} viewAll="/collection/new-arrivals" />;
      case 'bestsellers':
        return <ProductRail key="bestsellers" testId="rail-bestsellers" eyebrow={s.eyebrow || 'Best Sellers'} title={s.title || 'Signature pieces'} products={(data.bestsellers || []).slice(0, s.limit || 8)} viewAll="/collection/luxury-collection" />;
      case 'editors_picks':
        return <ProductRail key="editors_picks" testId="rail-editors" eyebrow={s.eyebrow || "Editor's Picks"} title={s.title || 'Handpicked by our stylists'} products={(data.editors_picks || []).slice(0, s.limit || 8)} viewAll="/collection/editors-picks" />;
      case 'shop_by_room':
        return (
          <section key="shop_by_room" className="container-gaze py-10 sm:py-14">
            <Reveal className="mb-6 flex items-end justify-between">
              <div><p className="eyebrow mb-2">{s.eyebrow || 'Shop by Room'}</p><h2 className="text-2xl font-semibold sm:text-3xl">{s.title || 'Curated for every space'}</h2></div>
              <Link to="/rooms" className="hidden items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))] sm:flex">All rooms <ArrowRight size={16} /></Link>
            </Reveal>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{rooms.slice(0, s.limit || 8).map((r) => <RoomTile key={r.id} room={r} />)}</div>
          </section>
        );
      case 'shop_by_style':
        return (
          <section key="shop_by_style" className="container-gaze py-10">
            <Reveal className="mb-6"><p className="eyebrow mb-2">{s.eyebrow || 'Shop by Style'}</p><h2 className="text-2xl font-semibold sm:text-3xl">{s.title || 'Find your aesthetic'}</h2></Reveal>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {styles.slice(0, s.limit || 10).map((st) => (
                <Link key={st.id} to={`/style/${st.slug}`} data-testid="style-browse-card" className="rounded-xl border border-border bg-white p-5 text-center transition hover:hairline-gold hover:shadow-md">
                  <span className="font-display text-lg">{st.name}</span>
                </Link>
              ))}
            </div>
          </section>
        );
      case 'brand_story':
        if (brand_story.enabled === false) return null;
        return (
          <section key="brand_story" className="container-gaze py-10 sm:py-16">
            <div className="grid items-center gap-8 overflow-hidden rounded-3xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] md:grid-cols-2">
              <div className="p-8 sm:p-12">
                <p className="eyebrow mb-3 !text-[hsl(var(--gaze-gold))]">The Gaze Philosophy</p>
                <h2 className="font-display text-3xl sm:text-4xl">{brand_story.title}</h2>
                <p className="mt-4 max-w-md text-white/80">{brand_story.text}</p>
                <Link to="/page/about" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gaze-gold))]/50 px-6 py-3 text-sm font-semibold text-[hsl(var(--gaze-gold))]">Discover our story <ArrowRight size={15} /></Link>
              </div>
              <div className="h-64 md:h-full"><img src={brand_story.image} alt="Brand story" className="h-full w-full object-cover" /></div>
            </div>
          </section>
        );
      case 'inspiration':
        return (
          <section key="inspiration" className="container-gaze py-10">
            <Reveal className="mb-6 flex items-end justify-between">
              <div><p className="eyebrow mb-2">{s.eyebrow || 'Inspiration Gallery'}</p><h2 className="text-2xl font-semibold sm:text-3xl">{s.title || 'Shop the look'}</h2></div>
              <Link to="/inspiration" className="flex items-center gap-1 text-sm font-semibold text-[hsl(var(--primary))]">Explore <ArrowRight size={16} /></Link>
            </Reveal>
            <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [column-fill:_balance]">
              {inspiration.map((img, i) => (
                <Link to="/inspiration" key={i} className="mb-4 block break-inside-avoid overflow-hidden rounded-2xl">
                  <img src={img} alt="Inspiration" loading="lazy" className={`w-full object-cover transition-transform duration-700 hover:scale-105 ${i % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}`} />
                </Link>
              ))}
            </div>
          </section>
        );
      case 'video_banner':
        if (video_banner.enabled === false) return null;
        return (
          <section key="video_banner" className="relative my-6 overflow-hidden">
            <div className="relative h-[46vh]">
              <img src={video_banner.image} alt="Craft" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                <div className="text-center text-white">
                  <p className="eyebrow mb-3 !text-[hsl(var(--gaze-gold))]">Craftsmanship</p>
                  <h2 className="font-display text-3xl sm:text-5xl">{video_banner.title}</h2>
                </div>
              </div>
            </div>
          </section>
        );
      case 'testimonials':
        return (
          <section key="testimonials" className="container-gaze py-12">
            <Reveal className="mb-8 text-center"><p className="eyebrow mb-2">{s.eyebrow || 'Loved by 50,000+ homes'}</p><h2 className="text-2xl font-semibold sm:text-3xl">{s.title || 'What our customers say'}</h2></Reveal>
            <div className="grid gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <div key={t.id} className="rounded-2xl border border-border bg-white p-6">
                  <Stars value={t.rating} />
                  <p className="mt-3 text-sm leading-relaxed text-foreground/80">&quot;{t.text}&quot;</p>
                  <p className="mt-4 text-sm font-semibold">{t.name}<span className="font-normal text-muted-foreground"> · {t.city}</span></p>
                </div>
              ))}
            </div>
          </section>
        );
      case 'instagram':
        return (
          <section key="instagram" className="container-gaze pb-14">
            <Reveal className="mb-6 text-center"><p className="eyebrow mb-2">{s.eyebrow || '@gazedecor'}</p><h2 className="text-2xl font-semibold sm:text-3xl">{s.title || 'Join the Gaze community'}</h2></Reveal>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {instagram.map((ig, i) => (
                <a key={i} href={ig.link} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-xl">
                  <img src={ig.image} alt="Instagram" loading="lazy" className="h-full w-full object-cover transition hover:scale-105" />
                </a>
              ))}
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* HERO */}
      {hero.enabled !== false && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImage} alt="Gaze Decor" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </div>
          <div className="container-gaze relative flex min-h-[78vh] flex-col justify-center py-20">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl text-white">
              <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gaze-gold))]/50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[hsl(var(--gaze-gold))]">
                <Sparkles size={13} /> {hero.eyebrow}
              </p>
              <h1 className="font-display text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">{hero.title}</h1>
              <p className="mt-5 max-w-lg text-base text-white/80 sm:text-lg">{hero.subtitle}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={hero.cta_link || '/shop'} data-testid="home-hero-primary-cta" className="rounded-full bg-[hsl(var(--gaze-gold))] px-7 py-3.5 text-sm font-semibold text-black transition hover:brightness-105">
                  {hero.cta}
                </Link>
                <Link to={hero.secondary_link || '/rooms'} className="rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-white/10">
                  {hero.secondary_cta}
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* TRUST BAR (always on) */}
      <div className="border-b border-border bg-white">
        <div className="container-gaze grid grid-cols-2 gap-4 py-5 md:grid-cols-4">
          {[[Truck, 'Free Shipping', 'On orders over ₹1,999'], [ShieldCheck, 'Cash on Delivery', 'Available pan-India'], [RotateCcw, 'Easy Returns', '7-day return window'], [Gift, 'Premium Gifting', 'Free gift message']].map(([Icon, t, sub], i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--gaze-ivory))]"><Icon size={18} className="text-[hsl(var(--primary))]" /></div>
              <div><p className="text-sm font-semibold">{t}</p><p className="text-xs text-muted-foreground">{sub}</p></div>
            </div>
          ))}
        </div>
      </div>

      {enabledSections.map(renderSection)}

      {/* FEATURED CATEGORIES (admin-managed) */}
      {featuredCats.length > 0 && (
        <section className="container-gaze py-10 sm:py-14" data-testid="home-featured-categories">
          <Reveal className="mb-6 flex items-end justify-between">
            <div><p className="eyebrow mb-2">Featured</p><h2 className="text-2xl font-semibold sm:text-3xl">Shop by category</h2></div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {featuredCats.map((c) => {
              const path = c.type === 'room' ? `/rooms/${c.slug}` : c.type === 'category' ? `/category/${c.slug}` : `/${c.type}/${c.slug}`;
              return (
                <Link to={path} key={c.id} data-testid="featured-category-card" className="group relative block overflow-hidden rounded-2xl">
                  <div className="aspect-[3/4] overflow-hidden bg-[hsl(var(--gaze-ivory))]">
                    <img src={c.image || c.hero_image} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-display text-xl text-white">{c.name}</h3>
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-white/80">Shop now <ArrowRight size={13} /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {recentProducts.length > 0 && (
        <ProductRail testId="rail-recent" eyebrow="Continue Browsing" title="Recently viewed" products={recentProducts} />
      )}
    </div>
  );
}
